/* eslint-disable react/prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { BadgeList } from '@wix/members-badge-lib';
import { ImageService } from '@wix/members-area-commons-ts';

import withTranslate from '../../hoc/with-translate';
import RoleIcons from '../../containers/role-icons';
import Blocked from '../blocked';
import styles from './member-box-desktop.scss';
import Avatar from '../avatar';
import { preventDefault } from '../../services/click-handling';
import { LAYOUT_LIST_VIEW, LAYOUT_FF_PAGE_VIEW, LAYOUT_GRID_VIEW } from '../../constants/layout-types';
import MemberMoreButton from '../member-more-button';
import ActionButtons from './action-buttons';
import MemberName from '../member-name';
import { Button } from '../button';

const getCoverUrl = ({ member, defaultCoverUrl }) => {
  const imageService = new ImageService();
  const fullDefaultCoverUrl = defaultCoverUrl && `https://static.wixstatic.com/media/${defaultCoverUrl}`;
  const coverUrl = member.cover ? imageService.getImageUrl({ image: member.cover }) : fullDefaultCoverUrl;

  return coverUrl ? { backgroundImage: `url('${coverUrl}')` } : null;
};

class Details extends React.Component {
  render() {
    const { member, goToMemberPage, showAsBlocked } = this.props;

    return (
      <div className={classNames(styles.name)}>
        <span>
          <MemberName name={member.name} member={member} goToMemberPage={goToMemberPage} className={styles.nameText} />
        </span>
        <RoleIcons
          withNames
          roles={member.roles}
          containerClassName={classNames(styles.roleIcons, 'ma-primary-font')}
          iconClassName="ma-fill-primary-text"
        />
        {showAsBlocked && (
          <Blocked member={member} align="center" className={classNames('ma-primary-font', styles.blocked)} />
        )}
      </div>
    );
  }
}

Details.propTypes = {
  member: PropTypes.object.isRequired,
  goToMemberPage: PropTypes.func.isRequired,
  showAsBlocked: PropTypes.bool,
};

const MoreActions = ({ member, rolesActions, onRoleAction, isRtlLocale }) => (
  <MemberMoreButton
    rolesActions={rolesActions}
    member={member}
    onRoleAction={onRoleAction}
    containerClass={styles.actions}
    iconContainerClass="ma-fill-primary-text"
    isRtlLocale={isRtlLocale}
  />
);

MoreActions.propTypes = {
  member: PropTypes.object.isRequired,
  rolesActions: PropTypes.array.isRequired,
  onRoleAction: PropTypes.func.isRequired,
  isRtlLocale: PropTypes.bool,
};

const Picture = ({ member, goToMemberPage, pictureStyle }) => (
  <div className={styles.pictureCont} onClick={() => goToMemberPage(member.uid)}>
    <Avatar
      url={member.picture}
      layoutType={pictureStyle}
      className={classNames(styles.avatar, 'ma-avatar-outer-border')}
      name={member.name}
      isMobile={false}
    />
  </div>
);

Picture.propTypes = {
  member: PropTypes.object.isRequired,
  goToMemberPage: PropTypes.func.isRequired,
  pictureStyle: PropTypes.number,
};

const Badges = ({ layoutStyle, badgesSettings, member, goToMemberPage }) =>
  badgesSettings.badges.length > 0 &&
  layoutStyle !== LAYOUT_FF_PAGE_VIEW && (
    <div className={styles.badgesContainer}>
      <BadgeList
        {...badgesSettings}
        maxRows={1}
        align={layoutStyle === LAYOUT_GRID_VIEW ? 'center' : 'left'}
        onClickMore={() => goToMemberPage(member.uid)}
        onBadgeClick={() => goToMemberPage(member.uid)}
      />
    </div>
  );

Badges.propTypes = {
  member: PropTypes.object.isRequired,
  layoutStyle: PropTypes.number.isRequired,
  goToMemberPage: PropTypes.func.isRequired,
  badgesSettings: PropTypes.object.isRequired,
};

const Cover = ({ member, goToMemberPage, defaultCoverUrl }) => (
  <div className={styles.coverCont} onClick={() => goToMemberPage(member.uid)}>
    <div
      className={classNames({
        [styles.cover]: true,
        'ma-members-cover-color': true,
        'ma-members-cover-default-photo-opacity': true,
        'ma-members-cover-card-corner-radius': true,
      })}
      style={getCoverUrl({ member, defaultCoverUrl })}
    />
  </div>
);

Cover.propTypes = {
  member: PropTypes.object.isRequired,
  goToMemberPage: PropTypes.func.isRequired,
};

const FF = ({ member, goToFFPage, t }) => (
  <div className={classNames(styles.follow, 'ma-members-ff-font')}>
    <Button
      onClick={preventDefault(() => goToFFPage(member.uid, true))}
      className={styles.followSide}
      dataHook="mb-followers"
    >
      <span>{member.followerCount}</span>
      <span>{t('profile-widget.followers')}</span>
    </Button>

    <div className={classNames(styles.followDivider, 'ma-members-list-divider-bg')} />
    <Button
      onClick={preventDefault(() => goToFFPage(member.uid, false))}
      className={styles.followSide}
      dataHook="mb-following"
    >
      <span>{member.followingCount}</span>
      <span>{t('profile-widget.following')}</span>
    </Button>
  </div>
);

FF.propTypes = {
  member: PropTypes.object.isRequired,
  goToFFPage: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

const CountsAndActions = ({
  showFollowers,
  member,
  goToFFPage,
  currentMemberUid,
  goToMemberPage,
  followOrUnfollow,
  t,
  showChat,
  handleChat,
  showFollowButton,
}) => (
  <div className={classNames(styles.countsAndActions, styles.rightSide)}>
    {showFollowers && <FF member={member} goToFFPage={goToFFPage} t={t} />}
    <div
      className={classNames(
        styles.actionsContainer,
        member.uid === currentMemberUid ? styles.narrowActionsContainer : styles.wideActionsContainer,
      )}
    >
      <div className={styles.mainActionContainer}>
        <ActionButtons
          containerClassName={styles.button}
          linkClassName={styles.buttonAction}
          member={member}
          currentMemberUid={currentMemberUid}
          goToMemberPage={goToMemberPage}
          followOrUnfollow={followOrUnfollow}
          showChat={showChat}
          handleChat={preventDefault(() => handleChat(member.uid))}
          showFollowButton={showFollowButton}
        />
      </div>
    </div>
  </div>
);

CountsAndActions.propTypes = {
  member: PropTypes.object.isRequired,
  showFollowers: PropTypes.bool,
  goToFFPage: PropTypes.func.isRequired,
  currentMemberUid: PropTypes.string,
  goToMemberPage: PropTypes.func.isRequired,
  followOrUnfollow: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  showChat: PropTypes.bool,
  showFollowButton: PropTypes.bool,
};

const MemberBox = ({
  member,
  t,
  layoutStyle,
  pictureStyle,
  showCover,
  showFollowers,
  goToMemberPage,
  goToFFPage,
  className,
  currentMemberUid,
  followOrUnfollow,
  showAsBlocked,
  rolesActions,
  onRoleAction,
  badgesSettings,
  isRtlLocale,
  showChat,
  handleChat,
  defaultCoverUrl,
  showFollowButton,
  showBadges,
  membersHaveBadges,
}) => {
  const isGridView = layoutStyle === LAYOUT_GRID_VIEW;
  const containerClassName = classNames(
    'ma-members-member-box',
    'ma-members-member-box-border-color',
    styles.container,
    { [styles.showCover]: showCover },
    { [styles.withoutBadges]: !showBadges || !membersHaveBadges },
    { [styles.withoutFollowers]: !showFollowers },
    { [styles.withoutButtons]: !showChat && !showFollowButton },
    { [styles.listView]: layoutStyle === LAYOUT_LIST_VIEW },
    { [styles.gridView]: isGridView },
    { [styles.ffPageView]: layoutStyle === LAYOUT_FF_PAGE_VIEW },
    className,
  );

  return (
    <div data-hook="memberBox" className={containerClassName}>
      {showCover && <Cover member={member} goToMemberPage={goToMemberPage} defaultCoverUrl={defaultCoverUrl} />}
      <div className={styles.padding}>
        <Picture member={member} goToMemberPage={goToMemberPage} pictureStyle={pictureStyle} />
        <div className={styles.nameAndBadges}>
          <Details
            member={member}
            layoutStyle={layoutStyle}
            goToMemberPage={goToMemberPage}
            showAsBlocked={showAsBlocked}
          />
          {showBadges && (
            <Badges
              member={member}
              layoutStyle={layoutStyle}
              goToMemberPage={goToMemberPage}
              badgesSettings={badgesSettings}
            />
          )}
        </div>

        <CountsAndActions
          showFollowers={showFollowers}
          member={member}
          goToFFPage={goToFFPage}
          currentMemberUid={currentMemberUid}
          goToMemberPage={goToMemberPage}
          followOrUnfollow={followOrUnfollow}
          t={t}
          showChat={showChat}
          handleChat={handleChat}
          showFollowButton={showFollowButton}
        />

        <MoreActions
          member={member}
          rolesActions={rolesActions}
          onRoleAction={onRoleAction}
          isRtlLocale={isRtlLocale}
        />
      </div>
    </div>
  );
};

MemberBox.propTypes = {
  member: PropTypes.object.isRequired,
  followOrUnfollow: PropTypes.func.isRequired,
  layoutStyle: PropTypes.number,
  pictureStyle: PropTypes.number,
  showCover: PropTypes.bool,
  currentMemberUid: PropTypes.string,
  t: PropTypes.func.isRequired,
  isSocial: PropTypes.bool,
  rolesActions: PropTypes.array.isRequired,
  showFollowers: PropTypes.bool,
  onRoleAction: PropTypes.func.isRequired,
  goToMemberPage: PropTypes.func.isRequired,
  goToFFPage: PropTypes.func.isRequired,
  className: PropTypes.string,
  showAsBlocked: PropTypes.bool,
  badgesSettings: PropTypes.object.isRequired,
  isRtlLocale: PropTypes.bool,
  showChat: PropTypes.bool,
  defaultCoverUrl: PropTypes.string,
  showFollowButton: PropTypes.bool,
  showBadges: PropTypes.bool,
  membersHaveBadges: PropTypes.bool,
};

export default withTranslate(MemberBox);
